import { useEffect, useMemo, useRef, useState } from "react";
import { useDataSource } from "@firecms/core";
import { getApp } from "firebase/app";
import {
  collection,
  doc,
  getFirestore,
  onSnapshot,
  type DocumentData,
} from "firebase/firestore";

import { eventTaskPath, parseEventTask, type EventTask } from "../collections/eventTask";
import {
  buildEventsPath,
  eventsCollection,
  type Event,
} from "../collections/events";
import {
  buildQuestionsCollection,
  buildQuestionsPath,
} from "../collections/questions";
import { buildUnitCollection, buildUnitsPath } from "../collections/units";
import {
  LEARNING_STAT_DIFFICULTIES,
  LEARNING_STATS_OVERVIEW_ID,
  aggregateLearningOverview,
  parseLearningDifficulty,
  type LearningOverview,
  type StoredDifficultyBucket,
  type StoredLearningOverview,
  type StoredQuestionStats,
  type StoredUnitLearningStats,
  type LearningCatalogQuestion,
  type LearningCatalogUnit,
  type LearningStatDifficulty,
} from "../eventContent/aggregateLearningOverview";
import { eventContentScopeId } from "../eventContent/eventContentScope";
import { useAdminTranslation } from "../i18n/useAdminTranslation";
import type { EventCollectionId } from "../workspace/collectionIds";

type Catalog = {
  units: LearningCatalogUnit[];
  questions: LearningCatalogQuestion[];
};

function asCount(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function parseBucket(raw: unknown): StoredDifficultyBucket {
  if (raw == null || typeof raw !== "object") {
    return {
      catalogQuestionCount: 0,
      answerCount: 0,
      correctCount: 0,
      uniqueParticipantCount: 0,
    };
  }
  const data = raw as DocumentData;
  return {
    catalogQuestionCount: asCount(data.catalogQuestionCount),
    answerCount: asCount(data.answerCount),
    correctCount: asCount(data.correctCount),
    uniqueParticipantCount: asCount(data.uniqueParticipantCount),
  };
}

function parseQuestion(raw: unknown): StoredQuestionStats | null {
  if (raw == null || typeof raw !== "object") {
    return null;
  }
  const data = raw as DocumentData;
  const difficulty = parseLearningDifficulty(data.difficulty);
  const questionCode =
    typeof data.questionCode === "string" ? data.questionCode : "";
  const questionId =
    typeof data.questionId === "string" ? data.questionId : questionCode;
  if (!difficulty || !questionCode) {
    return null;
  }
  return {
    questionId,
    questionCode,
    difficulty,
    answerCount: asCount(data.answerCount),
    correctCount: asCount(data.correctCount),
    topWrongOptionCode:
      typeof data.topWrongOptionCode === "string"
        ? data.topWrongOptionCode
        : undefined,
    topWrongOptionCount:
      typeof data.topWrongOptionCount === "number"
        ? data.topWrongOptionCount
        : undefined,
  };
}

function parseUnitStats(data: DocumentData): StoredUnitLearningStats {
  const difficulties = {} as Record<
    LearningStatDifficulty,
    StoredDifficultyBucket
  >;
  const rawDifficulties =
    data.difficulties != null && typeof data.difficulties === "object"
      ? (data.difficulties as DocumentData)
      : {};
  for (const difficulty of LEARNING_STAT_DIFFICULTIES) {
    difficulties[difficulty] = parseBucket(rawDifficulties[difficulty]);
  }
  const questions = Array.isArray(data.questions)
    ? data.questions.flatMap((item) => {
        const parsed = parseQuestion(item);
        return parsed ? [parsed] : [];
      })
    : [];
  return {
    unitCode: typeof data.unitCode === "string" ? data.unitCode : "",
    participantCount: asCount(data.participantCount),
    difficulties,
    questions,
  };
}

function parseOverview(data: DocumentData): StoredLearningOverview {
  return {
    participantCount: asCount(data.participantCount),
    answeredParticipantCount: asCount(data.answeredParticipantCount),
    answerCount: asCount(data.answerCount),
    correctCount: asCount(data.correctCount),
  };
}

function asLocalizedMap(value: unknown): Record<string, string> {
  if (value == null || typeof value !== "object") {
    return {};
  }
  const result: Record<string, string> = {};
  for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
    if (typeof entry === "string") {
      result[key] = entry;
    }
  }
  return result;
}

export function useLearningStatsOverview(eventId: EventCollectionId | null) {
  const dataSource = useDataSource();
  const { locale } = useAdminTranslation();
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [overviewDoc, setOverviewDoc] = useState<StoredLearningOverview | null>(
    null
  );
  const [statsByUnitId, setStatsByUnitId] = useState<
    Map<string, StoredUnitLearningStats>
  >(new Map());
  const [compute, setCompute] = useState<EventTask | null>(null);
  const [unpublished, setUnpublished] = useState(false);
  const [loading, setLoading] = useState(false);
  const loadGeneration = useRef(0);

  useEffect(() => {
    loadGeneration.current += 1;
    const generation = loadGeneration.current;

    if (!eventId) {
      setCatalog(null);
      setUnpublished(false);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setCatalog(null);
    setUnpublished(false);

    const load = async () => {
      try {
        const eventEntity = await dataSource.fetchEntity<Event>({
          path: buildEventsPath({ accountId: eventId.accountId }),
          collection: eventsCollection,
          entityId: eventId.eventId,
        });
        const catalogId = eventContentScopeId(
          eventId,
          eventEntity?.values?.contentReleaseRef
        );

        if (!catalogId) {
          if (cancelled) {
            return;
          }
          setUnpublished(true);
          setCatalog({ units: [], questions: [] });
          return;
        }

        const [units, questions] = await Promise.all([
          dataSource.fetchCollection({
            path: buildUnitsPath(catalogId),
            collection: buildUnitCollection(catalogId),
          }),
          dataSource.fetchCollection({
            path: buildQuestionsPath(catalogId),
            collection: buildQuestionsCollection(catalogId),
          }),
        ]);

        if (cancelled || generation !== loadGeneration.current) {
          return;
        }

        setCatalog({
          units: units.map((entity) => ({
            id: entity.id,
            code: String(entity.values?.code ?? ""),
            name: asLocalizedMap(entity.values?.name),
          })),
          questions: questions.map((entity) => {
            const unitRef = entity.values?.unitRef as { id?: string } | undefined;
            const options = Array.isArray(entity.values?.options)
              ? entity.values.options.flatMap((option: unknown) => {
                  if (option == null || typeof option !== "object") {
                    return [];
                  }
                  const row = option as { code?: unknown; text?: unknown };
                  if (typeof row.code !== "string") {
                    return [];
                  }
                  return [
                    {
                      code: row.code,
                      text: asLocalizedMap(row.text),
                    },
                  ];
                })
              : [];
            return {
              id: entity.id,
              code: String(entity.values?.code ?? ""),
              unitId: unitRef?.id ?? "",
              difficulty: parseLearningDifficulty(entity.values?.difficulty),
              prompt: asLocalizedMap(entity.values?.question),
              options,
            };
          }),
        });
      } finally {
        if (!cancelled && generation === loadGeneration.current) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [dataSource, eventId]);

  useEffect(() => {
    if (!eventId) {
      setCompute(null);
      setOverviewDoc(null);
      setStatsByUnitId(new Map());
      return;
    }

    const db = getFirestore(getApp());
    const unsubEvent = onSnapshot(
      doc(db, eventTaskPath(eventId.accountId, eventId.eventId, "learningStats")),
      (snapshot) => {
        setCompute(parseEventTask(snapshot.data()));
      }
    );
    const unsubStats = onSnapshot(
      collection(
        db,
        "accounts",
        eventId.accountId,
        "events",
        eventId.eventId,
        "learningStats"
      ),
      (snapshot) => {
        const next = new Map<string, StoredUnitLearningStats>();
        let nextOverview: StoredLearningOverview | null = null;
        for (const statsDoc of snapshot.docs) {
          if (statsDoc.id === LEARNING_STATS_OVERVIEW_ID) {
            nextOverview = parseOverview(statsDoc.data());
            continue;
          }
          next.set(statsDoc.id, parseUnitStats(statsDoc.data()));
        }
        setOverviewDoc(nextOverview);
        setStatsByUnitId(next);
      }
    );

    return () => {
      unsubEvent();
      unsubStats();
    };
  }, [eventId]);

  const overview: LearningOverview = useMemo(() => {
    if (!catalog) {
      return aggregateLearningOverview({
        units: [],
        questions: [],
        overview: null,
        statsByUnitId: new Map(),
        locale,
      });
    }
    return aggregateLearningOverview({
      ...catalog,
      overview: overviewDoc,
      statsByUnitId,
      locale,
    });
  }, [catalog, locale, overviewDoc, statsByUnitId]);

  return {
    overview,
    loading,
    catalogLoaded: catalog != null,
    unpublished,
    compute,
  };
}
